/**
 * 整改发起弹窗（标签页式）
 * Rectification Modal with Tabbed Layout
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Form,
  Tabs,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Col,
  message,
  Upload,
  Space,
  Alert,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { rectificationService } from '../services';
import { useProjects, useWorkItemTree } from '../hooks';
import {
  RectificationSource,
  RectificationSeverity,
  CheckType,
  RECTIFICATION_SOURCE_MAP,
  RECTIFICATION_SEVERITY_MAP,
  CHECK_TYPE_MAP,
} from '../types';
import dayjs from 'dayjs';

interface RectificationModalProps {
  visible: boolean;
  defaultWorkItemId?: string;
  defaultProjectId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

interface FormValues {
  projectId?: string;
  workItemId?: string;
  source?: RectificationSource;
  checkType?: CheckType;
  severity?: RectificationSeverity;
  deductionPercentage?: number;
  deadline?: dayjs.Dayjs;
  title?: string;
  description?: string;
  standardBasis?: string;
  acceptanceCriteria?: string;
  responsibleUserId?: string;
  responsibleUserName?: string;
  responsibleParty?: string;
  responsiblePhone?: string;
  responsibleTeam?: string;
  reworkLaborCost?: number;
  reworkMaterialCost?: number;
  reworkEquipmentCost?: number;
  reworkSubcontractCost?: number;
  coordinationExtraCost?: number;
  delayImpactCost?: number;
  issueAttachments?: { url: string; name: string }[];
}

const RectificationModal: React.FC<RectificationModalProps> = ({
  visible,
  defaultWorkItemId,
  defaultProjectId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile[]>([]);

  const { projects } = useProjects();
  const selectedProjectId = Form.useWatch('projectId', form) || defaultProjectId;
  const selectedSeverity = Form.useWatch('severity', form);
  const { tree: workItemTree } = useWorkItemTree({ projectId: selectedProjectId });

  useEffect(() => {
    if (visible) {
      if (defaultProjectId) {
        form.setFieldValue('projectId', defaultProjectId);
      }
      if (defaultWorkItemId) {
        form.setFieldValue('workItemId', defaultWorkItemId);
      }
      setAttachments([]);
    }
  }, [visible]);

  useEffect(() => {
    if (selectedSeverity && !form.getFieldValue('deductionPercentage')) {
      const deduction = RECTIFICATION_SEVERITY_MAP[selectedSeverity]?.deduction;
      if (deduction !== undefined) {
        form.setFieldValue('deductionPercentage', deduction);
      }
    }
  }, [selectedSeverity]);

  const totalEstimatedCost = useMemo(() => {
    const values = form.getFieldsValue();
    return (
      (values.reworkLaborCost || 0) +
      (values.reworkMaterialCost || 0) +
      (values.reworkEquipmentCost || 0) +
      (values.reworkSubcontractCost || 0) +
      (values.coordinationExtraCost || 0) +
      (values.delayImpactCost || 0)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.getFieldValue('reworkLaborCost'),
    form.getFieldValue('reworkMaterialCost'),
    form.getFieldValue('reworkEquipmentCost'),
    form.getFieldValue('reworkSubcontractCost'),
    form.getFieldValue('coordinationExtraCost'),
    form.getFieldValue('delayImpactCost'),
  ]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const submitData: Record<string, unknown> = {
        ...values,
        deadline: values.deadline?.toISOString(),
        issueAttachments: attachments
          .filter(a => a.status === 'done' && a.response)
          .map(a => ({ url: (a.response as any)?.url || a.url || '', name: a.name || '' })),
      };

      const response = await rectificationService.create(submitData);
      if (response.success) {
        message.success('整改发起成功');
        form.resetFields();
        setAttachments([]);
        onSuccess?.();
        onClose();
      }
    } catch (error: any) {
      if (!error.errorFields) {
        message.error(error.message || '创建失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setAttachments([]);
    onClose();
  };

  const tabItems = [
    {
      key: 'basic',
      label: '基本信息',
      children: (
        <>
          <Form.Item
            name="projectId"
            label="关联项目"
            rules={[{ required: true, message: '请选择项目' }]}
          >
            <Select
              placeholder="请选择项目"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {projects.map(p => (
                <Option key={p._id} value={p._id} label={p.name}>
                  {p.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="workItemId"
            label="关联分项工程"
            rules={[{ required: true, message: '请选择分项工程' }]}
          >
            <Select
              placeholder="请先选择项目，再选择分项工程"
              showSearch
              optionFilterProp="label"
              allowClear
              disabled={!selectedProjectId}
            >
              {workItemTree
                .filter(item => !item.parentItemId)
                .flatMap(parent =>
                  [
                    { ...parent, label: parent.name, value: parent._id },
                    ...(parent.children || []).map(child => ({
                      ...child,
                      label: `  └ ${child.name}`,
                      value: child._id,
                    })),
                  ]
                )
                .map(item => (
                  <Option key={item.value} value={item.value} label={item.label}>
                    {item.label}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="source"
                label="问题来源"
                rules={[{ required: true, message: '请选择问题来源' }]}
              >
                <Select placeholder="请选择问题来源">
                  {Object.entries(RECTIFICATION_SOURCE_MAP).map(([key, value]) => (
                    <Option key={key} value={key}>{value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="checkType" label="检查类型">
                <Select placeholder="请选择检查类型" allowClear>
                  {Object.entries(CHECK_TYPE_MAP).map(([key, value]) => (
                    <Option key={key} value={key}>{value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="severity"
                label="严重程度"
                rules={[{ required: true, message: '请选择严重程度' }]}
              >
                <Select
                  placeholder="请选择严重程度"
                  onChange={() => form.setFieldValue('deductionPercentage', undefined)}
                >
                  {Object.entries(RECTIFICATION_SEVERITY_MAP).map(([key, info]) => (
                    <Option key={key} value={key}>
                      {info.label}（扣除 {info.deduction}%）
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="deductionPercentage"
                label="扣除比例(%)"
                rules={[{ required: true, message: '请输入扣除比例' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  precision={0}
                  placeholder="由严重程度自动带出"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="deadline"
            label="整改期限"
            rules={[{ required: true, message: '请选择整改期限' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择整改期限"
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="responsibleUserId" label="责任人">
                <Select placeholder="请选择责任人" allowClear showSearch>
                  {/* 预留接口，后期可接入负责人列表 */}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="responsibleTeam" label="责任班组">
                <Input placeholder="请输入责任班组" />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: 'requirements',
      label: '整改要求',
      children: (
        <>
          <Form.Item name="title" label="整改标题" rules={[{ required: true, message: '请输入整改标题' }]}>
            <Input placeholder="请输入整改标题，如：P002-001 抹灰空鼓整改" />
          </Form.Item>

          <Form.Item name="standardBasis" label="规范依据">
            <Input placeholder="如：GB50204-2024《混凝土结构工程施工质量验收规范》" />
          </Form.Item>

          <Form.Item
            name="description"
            label="问题描述"
            rules={[{ required: true, message: '请输入问题描述' }]}
          >
            <TextArea rows={3} placeholder="请详细描述发现的质量问题" />
          </Form.Item>

          <Form.Item
            name="acceptanceCriteria"
            label="验收标准"
            extra="明确整改完成后如何才算通过验收"
          >
            <TextArea rows={3} placeholder="如：墙面垂直度≤4mm，平整度≤3mm，无空鼓现象" />
          </Form.Item>

          <Form.Item name="responsibleParty" label="责任单位">
            <Input placeholder="如：泥工班组-张三" />
          </Form.Item>

          <Form.Item name="responsiblePhone" label="责任人电话">
            <Input placeholder="请输入责任人联系电话" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'cost',
      label: '质量成本',
      children: (
        <>
          <Alert
            message="以下为预计整改成本，由发起人估算，仅供参考"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reworkLaborCost" label="劳务费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reworkMaterialCost" label="材料费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reworkEquipmentCost" label="机械费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reworkSubcontractCost" label="分包费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="coordinationExtraCost" label="协调费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="delayImpactCost" label="误工费（元）">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            message={`预计整改总成本：¥${totalEstimatedCost.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            type="info"
            showIcon
          />
        </>
      ),
    },
    {
      key: 'attachments',
      label: '附件照片',
      children: (
        <>
          <Alert
            message="上传问题现场照片，建议至少上传1张，作为整改证据留存"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Dragger
            fileList={attachments}
            onChange={({ fileList }) => setAttachments(fileList)}
            beforeUpload={() => false}
            multiple
            maxCount={10}
            accept="image/*"
            onRemove={file => {
              setAttachments(prev => prev.filter(a => a.uid !== file.uid));
              return true;
            }}
          >
            <Space direction="vertical">
              <UploadOutlined style={{ fontSize: 32, color: '#999' }} />
              <p>点击或拖拽上传问题照片</p>
              <p style={{ color: '#bbb', fontSize: 12 }}>支持 jpg/png/gif，建议每张不超过 5MB</p>
            </Space>
          </Dragger>
        </>
      ),
    },
  ];

  return (
    <Modal
      title="发起整改"
      open={visible}
      onCancel={handleClose}
      onOk={handleOk}
      okText="确认发起"
      confirmLoading={loading}
      width={680}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Tabs items={tabItems} defaultActiveKey="basic" />
      </Form>
    </Modal>
  );
};

export default RectificationModal;
